import { useState } from "react";
import { Archive, Check, ChevronDown, GitBranch, GitMerge, Lock, Plus, Radio } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, cn } from "@ncp/ui";
import type { BranchView } from "../types";

/** GitHub-style branch picker: main (protected) first, open branches with ahead/behind counts, merged and archived ones on request. */
export function BranchSwitcher({ branches, current, onSwitch, onCreate, canWrite }: { branches: BranchView[]; current: string; onSwitch: (name: string) => void; onCreate: () => void; canWrite: boolean }) {
  const { t } = useTranslation();
  const [showClosed, setShowClosed] = useState(false);
  const active = branches.find((b) => b.name === current);
  const open = branches.filter((b) => b.status === "Open");
  const closed = branches.filter((b) => b.status !== "Open");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="max-w-64 gap-1.5 font-mono text-xs">
          {active?.isProtected ? <Lock className="size-3.5 text-aeblack-700" /> : <GitBranch className="size-3.5 text-aegold-700" />}
          <span className="truncate">{current}</span>
          {active && !active.isProtected && (active.ahead || active.behind) ? <span className="font-sans text-[10px] text-muted-foreground">↑{active.ahead} ↓{active.behind}</span> : null}
          <ChevronDown className="size-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        <DropdownMenuLabel>{t("admin:builder.branches.title")}</DropdownMenuLabel>
        {open.map((b) => <BranchItem key={b.id} branch={b} current={current} onSwitch={onSwitch} />)}
        {closed.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setShowClosed((v) => !v); }} className="text-xs text-muted-foreground"><Archive /> {showClosed ? t("admin:builder.branches.hideClosed") : t("admin:builder.branches.showClosed", { count: closed.length })}</DropdownMenuItem>
            {showClosed ? closed.map((b) => <BranchItem key={b.id} branch={b} current={current} onSwitch={onSwitch} />) : null}
          </>
        ) : null}
        {canWrite ? <><DropdownMenuSeparator /><DropdownMenuItem onSelect={onCreate}><Plus /> {t("admin:builder.branches.new")}</DropdownMenuItem></> : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function BranchItem({ branch: b, current, onSwitch }: { branch: BranchView; current: string; onSwitch: (n: string) => void }) {
  const { t } = useTranslation();
  return (
    <DropdownMenuItem onSelect={() => onSwitch(b.name)} className="items-start">
      <span className="mt-0.5 w-4">{b.name === current ? <Check className="size-3.5" /> : null}</span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 font-mono text-xs">
          {b.isProtected ? <Lock className="size-3" /> : b.status === "Merged" ? <GitMerge className="size-3 text-success-600" /> : <GitBranch className="size-3" />}
          <span className={cn("truncate", b.status !== "Open" && "text-muted-foreground")}>{b.name}</span>
          {b.containsLive && b.isProtected ? <span className="inline-flex items-center gap-0.5 rounded bg-success-100 px-1 font-sans text-[9px] font-semibold text-success-800"><Radio className="size-2.5" />LIVE</span> : null}
          {!b.isProtected ? <span className="ms-auto font-sans text-[10px] text-muted-foreground">↑{b.ahead} ↓{b.behind}</span> : null}
        </span>
        <span className="block truncate text-[10.5px] text-muted-foreground">{b.status !== "Open" ? `${t(`admin:builder.branches.status.${b.status}`)}${b.mergedInto ? ` → ${b.mergedInto}` : ""} · ` : ""}{b.headMessage} · {b.headAuthor}</span>
      </span>
    </DropdownMenuItem>
  );
}
